// components/PhotoAnnotator.jsx - Draw annotations on photos
import React, { useState, useRef, useEffect, useCallback } from 'react';

const PhotoAnnotator = ({ photo, onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('arrow'); // 'arrow', 'circle', 'freehand', 'text'
  const [color, setColor] = useState('#ef4444'); // Red
  const [lineWidth, setLineWidth] = useState(3);
  const [annotations, setAnnotations] = useState([]);
  const [currentAnnotation, setCurrentAnnotation] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [scale, setScale] = useState(1);
  const imageRef = useRef(null);

  // Load image and set up canvas
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
      
      // Calculate scale to fit container
      if (containerRef.current && canvasRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerRef.current.clientHeight - 120; // Account for toolbar
        
        const scaleX = containerWidth / img.width;
        const scaleY = containerHeight / img.height;
        const newScale = Math.min(scaleX, scaleY, 1); // Don't scale up
        
        setScale(newScale);
        
        canvasRef.current.width = img.width * newScale;
        canvasRef.current.height = img.height * newScale;
        
        redrawCanvas();
      }
    };
    img.src = photo.previewUrl;
  }, [photo]);

  // Redraw canvas with image and annotations
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const img = imageRef.current;
    
    if (!ctx || !img) return;
    
    // Clear and draw image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    
    // Draw all saved annotations
    annotations.forEach(annotation => {
      drawAnnotation(ctx, annotation);
    });
    
    // Draw current annotation being created
    if (currentAnnotation) {
      drawAnnotation(ctx, currentAnnotation);
    }
  }, [annotations, currentAnnotation]);

  useEffect(() => {
    if (imageLoaded) {
      redrawCanvas();
    }
  }, [imageLoaded, annotations, currentAnnotation, redrawCanvas]);

  // Draw a single annotation
  const drawAnnotation = (ctx, annotation) => {
    ctx.strokeStyle = annotation.color;
    ctx.fillStyle = annotation.color;
    ctx.lineWidth = annotation.lineWidth * scale;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    switch (annotation.type) {
      case 'arrow':
        drawArrow(ctx, annotation);
        break;
      case 'circle':
        drawCircle(ctx, annotation);
        break;
      case 'freehand':
        drawFreehand(ctx, annotation);
        break;
      case 'text':
        drawText(ctx, annotation);
        break;
    }
  };

  const drawArrow = (ctx, { startX, startY, endX, endY }) => {
    const headLength = 15 * scale;
    const angle = Math.atan2(endY - startY, endX - startX);
    
    // Draw line
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();
    
    // Draw arrowhead
    ctx.beginPath();
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - headLength * Math.cos(angle - Math.PI / 6),
      endY - headLength * Math.sin(angle - Math.PI / 6)
    );
    ctx.lineTo(
      endX - headLength * Math.cos(angle + Math.PI / 6),
      endY - headLength * Math.sin(angle + Math.PI / 6)
    );
    ctx.closePath();
    ctx.fill();
  };

  const drawCircle = (ctx, { startX, startY, endX, endY }) => {
    const centerX = (startX + endX) / 2;
    const centerY = (startY + endY) / 2;
    const radiusX = Math.abs(endX - startX) / 2;
    const radiusY = Math.abs(endY - startY) / 2;
    
    ctx.beginPath();
    ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
    ctx.stroke();
  };

  const drawFreehand = (ctx, { points }) => {
    if (!points || points.length < 2) return;
    
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.stroke();
  };

  const drawText = (ctx, { x, y, text, color }) => {
    const fontSize = Math.max(16, 20 * scale);
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.fillStyle = color;
    
    // Draw background
    const metrics = ctx.measureText(text);
    const padding = 4;
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.fillRect(
      x - padding,
      y - fontSize - padding,
      metrics.width + padding * 2,
      fontSize + padding * 2
    );
    
    // Draw text
    ctx.fillStyle = color;
    ctx.fillText(text, x, y);
  };

  // Get position from event
  const getPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  // Mouse/touch handlers
  const handleStart = (e) => {
    e.preventDefault();
    const pos = getPosition(e);
    
    if (tool === 'text') {
      setTextPosition(pos);
      return;
    }
    
    setIsDrawing(true);
    
    if (tool === 'freehand') {
      setCurrentAnnotation({
        type: 'freehand',
        points: [pos],
        color,
        lineWidth
      });
    } else {
      setCurrentAnnotation({
        type: tool,
        startX: pos.x,
        startY: pos.y,
        endX: pos.x,
        endY: pos.y,
        color,
        lineWidth
      });
    }
  };

  const handleMove = (e) => {
    if (!isDrawing || !currentAnnotation) return;
    e.preventDefault();
    
    const pos = getPosition(e);
    
    if (tool === 'freehand') {
      setCurrentAnnotation(prev => ({
        ...prev,
        points: [...prev.points, pos]
      }));
    } else {
      setCurrentAnnotation(prev => ({
        ...prev,
        endX: pos.x,
        endY: pos.y
      }));
    }
  };

  const handleEnd = () => {
    if (isDrawing && currentAnnotation) {
      setAnnotations(prev => [...prev, currentAnnotation]);
      setCurrentAnnotation(null);
    }
    setIsDrawing(false);
  };

  // Add text annotation
  const addText = () => {
    if (textInput.trim() && textPosition) {
      setAnnotations(prev => [...prev, {
        type: 'text',
        x: textPosition.x,
        y: textPosition.y,
        text: textInput.trim(),
        color
      }]);
      setTextInput('');
      setTextPosition(null);
    }
  };

  // Undo last annotation
  const undo = () => {
    setAnnotations(prev => prev.slice(0, -1));
  };

  // Save annotated image
  const save = () => {
    const canvas = canvasRef.current;
    
    // Create full-resolution canvas
    const fullCanvas = document.createElement('canvas');
    fullCanvas.width = imageRef.current.width;
    fullCanvas.height = imageRef.current.height;
    const fullCtx = fullCanvas.getContext('2d');
    
    // Draw image at full resolution
    fullCtx.drawImage(imageRef.current, 0, 0);
    
    // Draw annotations scaled up
    const upScale = imageRef.current.width / canvas.width;
    annotations.forEach(annotation => {
      const scaledAnnotation = scaleAnnotation(annotation, upScale);
      fullCtx.strokeStyle = scaledAnnotation.color;
      fullCtx.fillStyle = scaledAnnotation.color;
      fullCtx.lineWidth = scaledAnnotation.lineWidth;
      fullCtx.lineCap = 'round';
      fullCtx.lineJoin = 'round';
      drawAnnotation(fullCtx, scaledAnnotation);
    });
    
    // Convert to blob
    fullCanvas.toBlob((blob) => {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const file = new File([blob], `annotated-${timestamp}.jpg`, { type: 'image/jpeg' });
      const previewUrl = URL.createObjectURL(blob);
      
      onSave({
        ...photo,
        file,
        previewUrl,
        annotated: true,
        annotations: annotations.length
      });
    }, 'image/jpeg', 0.9);
  };

  // Scale annotation coordinates
  const scaleAnnotation = (annotation, factor) => {
    if (annotation.type === 'freehand') {
      return {
        ...annotation,
        points: annotation.points.map(p => ({ x: p.x * factor, y: p.y * factor })),
        lineWidth: annotation.lineWidth * factor
      };
    } else if (annotation.type === 'text') {
      return {
        ...annotation,
        x: annotation.x * factor,
        y: annotation.y * factor
      };
    } else {
      return {
        ...annotation,
        startX: annotation.startX * factor,
        startY: annotation.startY * factor,
        endX: annotation.endX * factor,
        endY: annotation.endY * factor,
        lineWidth: annotation.lineWidth * factor
      };
    }
  };

  const tools = [
    { id: 'arrow', icon: '➡️', label: 'Arrow' },
    { id: 'circle', icon: '⭕', label: 'Circle' },
    { id: 'freehand', icon: '✏️', label: 'Draw' },
    { id: 'text', icon: '📝', label: 'Text' }
  ];

  const colors = [
    '#ef4444', // Red
    '#f97316', // Orange
    '#eab308', // Yellow
    '#22c55e', // Green
    '#3b82f6', // Blue
    '#000000'  // Black
  ];

  return (
    <div 
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#1f2937',
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#111827',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <button
          onClick={onCancel}
          style={{
            padding: '8px 16px',
            backgroundColor: '#374151',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <span style={{ color: 'white', fontWeight: '500' }}>Annotate Photo</span>
        <button
          onClick={save}
          style={{
            padding: '8px 16px',
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Save
        </button>
      </div>

      {/* Tools */}
      <div style={{
        padding: '8px 16px',
        backgroundColor: '#111827',
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        {/* Tool selection */}
        {tools.map(t => (
          <button
            key={t.id}
            onClick={() => setTool(t.id)}
            style={{
              padding: '8px 12px',
              backgroundColor: tool === t.id ? '#2563eb' : '#374151',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <span>{t.icon}</span>
            <span style={{ fontSize: '12px' }}>{t.label}</span>
          </button>
        ))}
        
        {/* Divider */}
        <div style={{ width: '1px', height: '30px', backgroundColor: '#4b5563', margin: '0 8px' }} />
        
        {/* Color selection */}
        {colors.map(c => (
          <button
            key={c}
            onClick={() => setColor(c)}
            style={{
              width: '28px',
              height: '28px',
              backgroundColor: c,
              border: color === c ? '3px solid white' : '2px solid #6b7280',
              borderRadius: '50%',
              cursor: 'pointer'
            }}
          />
        ))}
        
        {/* Undo */}
        <button
          onClick={undo}
          disabled={annotations.length === 0}
          style={{
            padding: '8px 12px',
            backgroundColor: '#374151',
            color: annotations.length === 0 ? '#6b7280' : 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: annotations.length === 0 ? 'not-allowed' : 'pointer',
            marginLeft: 'auto'
          }}
        >
          ↩️ Undo
        </button>
      </div>

      {/* Canvas area */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        padding: '16px'
      }}>
        <canvas
          ref={canvasRef}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            touchAction: 'none',
            cursor: tool === 'text' ? 'text' : 'crosshair'
          }}
        />
      </div>

      {/* Text input modal */}
      {textPosition && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '400px'
          }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Add Text</h3>
            <input
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Enter text..."
              autoFocus
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                marginBottom: '16px',
                boxSizing: 'border-box'
              }}
              onKeyPress={(e) => e.key === 'Enter' && addText()}
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setTextPosition(null);
                  setTextInput('');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#e5e7eb',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={addText}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoAnnotator;